import React from 'react';
import Section from './Section';
import ServiceStatusIcons from './ServiceStatusIcons';
import { Patient, getPatientActivityStatus } from '../services/patientsService';
import { useIsMobile } from '../hooks/useIsMobile';

interface PatientProfileHeaderProps {
  patient: Patient;
  onEditData: () => void;
  onChangePassword: () => void;
  onDelete: () => void;
}

// Compact, full-width identity bar: avatar, name, contact, activity + service
// status, and the primary edit action. Detailed fields live in the "Perfil" tab.
const PatientProfileHeader: React.FC<PatientProfileHeaderProps> = ({ patient, onEditData, onChangePassword, onDelete }) => {
  const isMobile = useIsMobile();
  const activity = getPatientActivityStatus(patient);

  const initials = `${(patient.firstName || '').charAt(0)}${(patient.lastName || '').charAt(0)}`.toUpperCase();

  const lastActivity = patient.lastActivityAt
    ? new Date(patient.lastActivityAt).toLocaleDateString('es-ES', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : 'Nunca';

  const activityChip = (() => {
    if (activity === 'never') {
      return (
        <span style={chip('#fef2f2', '#991b1b', '#fca5a5')}>🚫 Nunca ha accedido</span>
      );
    }
    if (activity === 'inactive') {
      const date = patient.lastActivityAt
        ? new Date(patient.lastActivityAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
        : null;
      return (
        <span style={chip('#fffbeb', '#92400e', '#fbbf24')}>⚠️ Inactivo{date ? ` · ${date}` : ''}</span>
      );
    }
    return <span style={chip('#ecfdf5', '#047857', '#6ee7b7')}>✅ Activo</span>;
  })();

  return (
    <Section style={{ marginBottom: 24 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: isMobile ? 16 : 20,
        }}
      >
        {/* Avatar */}
        <div style={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
          {patient.avatarUrl ? (
            <img
              src={patient.avatarUrl}
              alt={`${patient.firstName} ${patient.lastName}`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <div style={{ width: '100%', height: '100%', background: '#38b2ac', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, color: '#fff' }}>
              {initials}
            </div>
          )}
        </div>

        {/* Identity */}
        <div style={{ minWidth: 0, flex: isMobile ? 'unset' : 1 }}>
          <div style={{ fontWeight: 700, fontSize: 20, color: '#1f2937' }}>
            {patient.firstName || ''} {patient.lastName || ''}
          </div>
          <div style={{ color: '#6b7280', fontSize: 14, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
            @{patient.username || ''}{patient.email ? ` · ${patient.email}` : ''}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 6, fontSize: 12.5, color: '#6b7280' }}>
            <span>🕐 Última actividad: <strong style={{ color: '#374151' }}>{lastActivity}</strong></span>
            <span>📱 App: <strong style={{ color: '#374151' }}>{patient.app_version || '-'}</strong></span>
          </div>
        </div>

        {/* Statuses */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {activityChip}
          {patient.serviceStatus && <ServiceStatusIcons status={patient.serviceStatus} size={26} gap={8} />}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
          <button
            onClick={onEditData}
            style={{
              background: '#38b2ac', color: '#fff', border: 'none', borderRadius: 6,
              padding: '9px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14,
              flex: isMobile ? '1 1 100%' : '0 0 auto',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#319795'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#38b2ac'; }}
          >
            Editar Datos
          </button>
          <button
            onClick={onChangePassword}
            style={{
              background: 'transparent', color: '#d97706', border: '1px solid #f59e0b', borderRadius: 6,
              padding: '9px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 14,
              flex: isMobile ? '1 1 0' : '0 0 auto',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fffbeb'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            Contraseña
          </button>
          <button
            onClick={onDelete}
            style={{
              background: 'transparent', color: '#e53e3e', border: '1px solid #e53e3e', borderRadius: 6,
              padding: '9px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 14,
              flex: isMobile ? '1 1 0' : '0 0 auto',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fff5f5'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            Eliminar
          </button>
        </div>
      </div>
    </Section>
  );
};

const chip = (bg: string, color: string, border: string): React.CSSProperties => ({
  display: 'inline-block',
  background: bg,
  color,
  border: `1px solid ${border}`,
  fontSize: 12,
  fontWeight: 600,
  padding: '4px 10px',
  borderRadius: 14,
  whiteSpace: 'nowrap',
});

export default PatientProfileHeader;
